// helpers/TreeHelpers.js

import { ViewAccess }   from "./../Enums.js";
import { canWriteView } from "../permissions/Permissions.js";
import { viewTypeRank, viewCategory, compareViewsForDisplay } from "./ViewHelpers.js";

// This helper builds the tree data, stripping any trailing '*' from project tokens for matching
export function buildTreeData({
  adminTokens,
  currentAccountToken,
  accounts,
  projects,
  forms,
  currentProjectToken,
  selectedViewLabel,
  currentUserId,
  isSuperAdmin,
}) {
  if (!adminTokens || !projects || !accounts) return [];

  const VIEW_TYPE_LABELS = {
    submissions: "Submissions",
    submissions_under_review: "Under review",
    reviews: "Reviews",
    users: "Users",
  };

  const getTypeLabel = (type) => VIEW_TYPE_LABELS[type] || type;

  const accountKeys = Object.keys(adminTokens);
  const multiAccount = accountKeys.length > 1;
  const accountToUse = multiAccount ? currentAccountToken : accountKeys[0];

  const projectTokensForAccount = adminTokens[accountToUse] || [];
  const cleanTokensForAccount = projectTokensForAccount.map((t) => t.replace(/\*$/, ""));

  const filteredProjects = projects.filter((project) =>
    cleanTokensForAccount.includes(project.token)
  );

  const buildProjectNode = (project) => {
    const viewsFlat =
      project.token === currentProjectToken
        ? forms
            .filter((form) => form.projectToken === project.token)
            .flatMap((form) =>
              (form?.meta?.views ?? []).map((view) => {
                const category = viewCategory(view, currentUserId);
                const isLocked = view.write === ViewAccess.NONE;

                const canEdit = canWriteView({
                  write: view.write,
                  owner: view.owner,
                  userId: currentUserId,
                  isSuperAdmin,
                });

                return {
                  value: view.viewLabel,
                  label: view.viewLabel,
                  type: view.type,
                  defaultView: !!view.defaultView,
                  owner: view.owner,
                  read: view.read,
                  write: view.write,
                  isMine: category === "mine",
                  isSystemView: category === "system",
                  isLocked,
                  canEdit,
                  isSharedWithMe: category === "shared",
                };
              })
            )
        : [];

    const byType = new Map();

    for (const view of viewsFlat) {
      const existing = byType.get(view.type) || [];
      existing.push(view);
      byType.set(view.type, existing);
    }

    const viewSections = Array.from(byType.entries())
      .map(([type, views]) => ({
        type,
        typeLabel: getTypeLabel(type),
        order: viewTypeRank(type),
        sectionId: `type:${project.token}:${type}`,
        views: [...views].sort(compareViewsForDisplay(currentUserId)),
      }))
      .sort((a, b) => a.order - b.order || a.typeLabel.localeCompare(b.typeLabel));

    const defaultView = selectedViewLabel || (viewsFlat[0]?.label ?? "");

    return {
      value: project.token,
      label: project.name,
      defaultView,
      viewSections,
    };
  };

  const projectsTree = filteredProjects
    .map(buildProjectNode)
    .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));

  if (multiAccount) {
    const accountsTree = accountKeys
      .map((accountToken) => {
        const accountObj = accounts.find((acct) => acct.token === accountToken) || {};
        const tokensForAccount = adminTokens[accountToken] || [];
        const cleanTokensForThisAccount = tokensForAccount.map((t) => t.replace(/\*$/, ""));

        const accountProjects = projectsTree.filter((project) =>
          cleanTokensForThisAccount.includes(project.value)
        );

        return {
          value: accountToken,
          label: accountObj.name || accountToken,
          projects: accountProjects,
        };
      })
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));

    return accountsTree;
  }

  return projectsTree;
}

// This helper builds an extended tree data with fallback to tokens
export function buildCloneProjectTreeData({ adminTokens, accounts, projects }) {
  if (!adminTokens || !accounts) return [];

  const accountKeys = Object.keys(adminTokens);

  const accountsTree = accountKeys.map(accountToken => {
    const accountObj = accounts.find(acct => acct.token === accountToken) || {};
    const tokensForAccount = adminTokens[accountToken] || [];

    const accountProjects = tokensForAccount
      .map(rawToken => {
        // strip trailing '*' if present
        const cleanToken = rawToken.replace(/\*$/, "");
        const projectObj = projects.find(proj => proj.token === cleanToken);
        return {
          value: cleanToken,
          label: projectObj?.name || cleanToken,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return {
      value: accountToken,
      label: accountObj.name || accountToken,
      projects: accountProjects,
    };
  }).sort((a, b) => a.label.localeCompare(b.label));

  return accountsTree;
}

// buildViewPaneData.js
//
// Minimal selector for the Views pane (pills).
// - Mirrors buildTreeData token handling, sorting, and defaulting.
// - No pages, no pool — just the views for the selected form in the current project.
//
export function buildViewPaneData({
  adminTokens,              // { [accountToken]: string[] } (tokens may end with '*')
  currentAccountToken,      // string
  accounts,                 // [{ token, name, ... }]
  projects,                 // [{ token, name, ... }]
  forms,                    // [{ projectToken, formToken|value, meta:{views:[...]}, ... }]
  currentProjectToken,      // string
  currentFormToken,         // string (optional; if omitted, first form in project is used)
  selectedViewLabel,        // string (optional preference)
}) {
  if (!adminTokens || !projects || !accounts) {
    return {
      views: [],                // [{ value, label, defaultView, type }]
      effectiveViewLabel: null, // the pill that should be selected
      formToken: null,
      projectToken: null,
    };
  }

  // --- helpers consistent with buildTreeData
  const stripTrailingStar = (t) => (typeof t === "string" ? t.replace(/\*$/, "") : t);
  const sortByLabel = (a, b) => String(a.label || "").localeCompare(String(b.label || ""));

  // Resolve account context (single vs multi)
  const accountKeys = Object.keys(adminTokens);
  const multiAccount = accountKeys.length > 1;
  const accountToUse = multiAccount ? currentAccountToken : accountKeys[0];

  // Allowed project tokens for the active account (strip trailing '*')
  const allowed = (adminTokens[accountToUse] || []).map(stripTrailingStar);

  // Filter projects by allowed tokens
  const filteredProjects = (projects || []).filter(p => allowed.includes(p.token));

  // Current project within allowed set
  const currentProject = filteredProjects.find(p => p.token === currentProjectToken) || null;
  if (!currentProject) {
    return { views: [], effectiveViewLabel: null, formToken: null, projectToken: null };
  }

  // Forms for the current project
  const formsForProject = (forms || []).filter(f => f.projectToken === currentProject.token);

  // Pick current form by token or fallback to first (sorted by label for stability, if label exists)
  const pickForm = () => {
    if (currentFormToken) {
      const byToken = formsForProject.find(f => (f.formToken || f.value) === currentFormToken);
      if (byToken) return byToken;
    }
    // fallback: first by label/value
    const sorted = formsForProject.slice().sort((a, b) =>
      String(a.label || a.name || a.value || "").localeCompare(String(b.label || b.name || b.value || ""))
    );
    return sorted[0] || null;
  };

  const form = pickForm();
  if (!form) {
    return { views: [], effectiveViewLabel: null, formToken: null, projectToken: currentProject.token };
  }

  // Normalize views for this form (your buildTreeData uses form.meta.views)
  const rawViews = Array.isArray(form?.meta?.views) ? form.meta.views : [];
  const views = rawViews
    .map(v => ({
      value: v.viewLabel || v.label || v.value,
      label: v.viewLabel || v.label || v.value,
      defaultView: !!v.defaultView,
      type: v.type || "submissions",
    }))
    .sort(sortByLabel);

  // Choose pill selection: selectedViewLabel → explicit defaultView → first
  let effectiveViewLabel = null;
  if (selectedViewLabel && views.some(v => v.label === selectedViewLabel)) {
    effectiveViewLabel = selectedViewLabel;
  } else {
    const flagged = views.find(v => v.defaultView);
    effectiveViewLabel = (flagged || views[0] || {}).label || null;
  }

  // TODO (validation): enforce unique viewLabel within a form; ensure exactly one defaultView === true.

  return {
    views,                         // array for pills
    effectiveViewLabel,            // which pill to mark active
    formToken: form.formToken || form.value || null,
    projectToken: currentProject.token,
  };
}

export function buildProjectFormsMinimal({
  forms,                // [{ id, formToken|value, token, version|_version, projectToken, pages:[], meta:{ views:[] } }]
  currentProjectToken,  // string
}) {
  if (!Array.isArray(forms) || !currentProjectToken) return [];

  return forms
    .filter(f => f?.projectToken === currentProjectToken)
    .map(f => {
      const viewsArray = Array.isArray(f?.meta?.views) ? f.meta.views : [];
      const pagesArray = Array.isArray(f?.pages) ? f.pages : [];

      return {
        id: f?.id ?? f?._id ?? null,
        formToken: f?.formToken ?? f?.value ?? null,
        token: f?.token ?? null,
        version: f?.version ?? f?._version ?? null,
        projectToken: f?.projectToken ?? currentProjectToken,  // keep it; correct fallback

        // keep everything from meta.views; shallow-clone to avoid shared refs
        views: viewsArray.map(v => (v && typeof v === "object" ? { ...v } : v)),
        // pages is a root array; shallow-clone similarly
        pages: pagesArray.map(p => (p && typeof p === "object" ? { ...p } : p )),
      };
    });
}
